// src/services/invoiceService.js
// 78차 62차: branchIdx 파라미터 추가 — 지점별로 plan/수수료 조건이 다를 수 있어 인보이스도
// 지점 단위로 정확히 반영.
// 78차 32차: 신규 — 매장관리자용 월별 정산 인보이스(PDF, 비공식 문서). "정식 전자세금계산서"
// (팝빌/바로빌 등 대행 서비스 연동)는 별도 계약/행정 절차가 필요한 영역이라 범위 밖 —
// 이건 매장이 자체적으로 보관/참고할 수 있는 정산 내역서 성격의 PDF만 다룬다.
// 최근 1년치까지만 다운로드 가능(그 이전 달은 UI(monthOptions)에서도 애초에 선택 불가하지만,
// 방어적으로 한 번 더 확인).
import { Platform } from 'react-native';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import { calcStoreFee, getPlanFee, getPlanFeeLabel, getPickAddonFee, getOpsAddonFee, getStoreThemeCount } from './feeService';
import { getStoreDisplayName } from './productService';
import { todayStr } from '../utils/dateUtils';

const won = (n) => `${Math.round(n || 0).toLocaleString()}원`;

export function isMonthWithinInvoiceRange(month) {
  const target = new Date(`${month}-01T00:00:00`);
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  oneYearAgo.setDate(1);
  return target >= oneYearAgo;
}

function buildInvoiceHtml({ store, month, revenue, reservationCount, themeCount, paidPickSlotCount = 0, branchIdx = null }) {
  const fee = calcStoreFee(store, revenue, themeCount, branchIdx);
  const planFee = getPlanFee(store, branchIdx);
  const b = branchIdx != null ? store?.branches?.[branchIdx] : null;
  const feeType = (b && b.feeType) || store?.feeType;
  const discountRate = (b && b.discountRate != null) ? b.discountRate : (store?.discountRate || 0);
  // 78차 39차: PICK 추가 슬롯 요금이 이 인보이스 계산에는 빠져 있어 정산 화면(StatsTab)의
  // 총액과 어긋나던 것 — calcTotalOwed와 동일하게 포함.
  const pickAddonFee = getPickAddonFee(paidPickSlotCount);
  // 80차 후속: [버그 수정, 실제 청구 누락] opsAddonFee(매장관리자 기능 애드온)도 동일하게
  // 빠져 있었음 — calcTotalOwed/서버(scheduledMonthlySettlement)는 이미 포함하는데
  // 이 PDF 인보이스만 안 더해서, 애드온 켜진 매장이 다운로드하는 정산 내역서 총액이
  // 실제 청구액보다 15,000원 적게 나오고 있었음.
  const opsAddonFee = getOpsAddonFee(store, branchIdx);
  const total = fee + planFee + opsAddonFee + pickAddonFee;
  const storeName = getStoreDisplayName(store);
  const issuedAt = todayStr();

  return `
  <html>
  <head>
    <meta charset="utf-8" />
    <style>
      body { font-family: -apple-system, sans-serif; color: #1a1a1a; padding: 40px; }
      h1 { font-size: 20px; letter-spacing: 2px; color: #b8860b; margin-bottom: 4px; }
      .sub { color: #777; font-size: 12px; margin-bottom: 32px; }
      table { width: 100%; border-collapse: collapse; margin-top: 16px; }
      th, td { text-align: left; padding: 10px 8px; border-bottom: 1px solid #eee; font-size: 13px; }
      th { color: #888; font-weight: 600; }
      .amount { text-align: right; }
      .total-row td { font-weight: 800; font-size: 15px; border-top: 2px solid #1a1a1a; border-bottom: none; }
      .notice { margin-top: 40px; font-size: 11px; color: #999; line-height: 1.6; }
    </style>
  </head>
  <body>
    <h1>UNLOCK 정산 내역서</h1>
    <div class="sub">발행일 ${issuedAt} · 대상 매장 ${storeName} · 정산월 ${month}</div>
    <table>
      <tr><th>항목</th><th class="amount">내용</th></tr>
      <tr><td>대상 월</td><td class="amount">${month}</td></tr>
      <tr><td>총 예약 건수</td><td class="amount">${reservationCount.toLocaleString()}건</td></tr>
      <tr><td>총 매출</td><td class="amount">${won(revenue)}</td></tr>
      <tr><td>플랫폼 수수료 (${feeType === 'fixed' ? '정액' : `요율 ${discountRate}%`})</td><td class="amount">${won(fee)}</td></tr>
      <tr><td>플랜 구독료 (${getPlanFeeLabel(store, branchIdx)})</td><td class="amount">${won(planFee)}</td></tr>
      ${opsAddonFee > 0 ? `<tr><td>매장관리자 기능 애드온</td><td class="amount">${won(opsAddonFee)}</td></tr>` : ''}
      ${pickAddonFee > 0 ? `<tr><td>PICK 추가 요금 (${paidPickSlotCount}개)</td><td class="amount">${won(pickAddonFee)}</td></tr>` : ''}
      <tr class="total-row"><td>정산 총액</td><td class="amount">${won(total)}</td></tr>
    </table>
    <div class="notice">
      본 문서는 UNLOCK 앱 내 데이터를 기준으로 자동 생성된 참고용 정산 내역서이며,
      세금계산서 등 법적 효력을 갖는 문서가 아닙니다.
    </div>
  </body>
  </html>`;
}

export async function generateAndShareInvoice({ store, month, revenue, reservationCount, themeCount, paidPickSlotCount = 0, branchIdx = null }) {
  if (!isMonthWithinInvoiceRange(month)) {
    throw new Error('최근 1년 이내 정산월만 인보이스를 다운로드할 수 있어요.');
  }
  const html = buildInvoiceHtml({ store, month, revenue, reservationCount, themeCount: themeCount ?? getStoreThemeCount(store), paidPickSlotCount, branchIdx });

  // 78차 39차: 실제 버그 발견 — expo-print의 웹 구현은 printToFileAsync()가 PDF를 만드는
  // 게 아니라 그냥 window.print()만 호출하고 아무 것도 반환하지 않음. 그런데 기존 코드가
  // `const { uri } = await Print.printToFileAsync(...)`로 반환값을 무조건 구조분해하고
  // 있어서, 웹에서는 undefined를 구조분해하다가 즉시 TypeError로 죽어 "다운로드 실패" 알림만
  // 뜨고 있었음. 웹에서는 새 창에 인보이스 HTML을 직접 띄우고 그 창에서 인쇄(‘PDF로 저장’
  // 선택 가능)하도록 분기 — 네이티브(iOS/Android)는 기존 방식 그대로.
  if (Platform.OS === 'web') {
    const win = window.open('', '_blank');
    if (!win) throw new Error('팝업이 차단됐어요. 브라우저의 팝업 차단을 해제해주세요.');
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 300);
    return null;
  }

  const { uri } = await Print.printToFileAsync({ html });
  if (await Sharing.isAvailableAsync()) {
    await Sharing.shareAsync(uri, { mimeType: 'application/pdf', UTI: 'com.adobe.pdf' });
  }
  return uri;
}
